import { useEffect, useMemo, useRef, useState } from 'react';
import { api, errorMessage } from '../lib/api.ts';
import { X } from '../lib/icons.tsx';
import type { CaseStudyCard, CaseStudyImportResponse, CaseStudyIndexResponse } from '../../shared/api.ts';

/**
 * "Pick a case study" — the front half of the Library's importer.
 *
 * The list is a cache of rkh.co.uk's own index, so this opens on what the
 * server already knows and refreshes behind the list rather than in front of
 * it: a picker that made someone wait on six page fetches before showing
 * anything would be slower than finding the page by hand, which is the thing it
 * replaces.
 *
 * Picking one is not free — it fetches the page and downloads its pictures and
 * clips, which is tens of megabytes on a video-led case study — so the pick is
 * an explicit click and the result says what it found before anyone commits to
 * an import.
 *
 * Deliberately knows nothing about where the result goes: it takes what it
 * found and hands it up. That is what let it move here from the new-deck wizard
 * without changing a prop.
 */

type Props = {
  picked: CaseStudyCard | null;
  imported: CaseStudyImportResponse | null;
  importing: boolean;
  importError: string | null;
  onPick: (card: CaseStudyCard) => void;
  onClear: () => void;
  disabled?: boolean;
};

/** "2 hours ago", for the freshness stamp. Deliberately coarse: the list
 *  changes when someone publishes a case study, which is not a daily event. */
function ago(iso: string | null): string {
  if (!iso) return 'never';
  const ms = Date.now() - Date.parse(iso);
  if (!Number.isFinite(ms) || ms < 0) return 'just now';
  const mins = Math.round(ms / 60000);
  if (mins < 2) return 'just now';
  if (mins < 60) return `${mins} minutes ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

export default function CaseStudyPicker({
  picked,
  imported,
  importing,
  importError,
  onPick,
  onClear,
  disabled = false,
}: Props) {
  const [cards, setCards] = useState<CaseStudyCard[]>([]);
  const [syncedAt, setSyncedAt] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  // The auto-sync is a per-mount decision, not a per-render one: without this a
  // failed sync that cleared `syncing` would immediately qualify for another.
  const asked = useRef(false);

  async function sync(): Promise<void> {
    setSyncing(true);
    try {
      const res = await api.post<CaseStudyIndexResponse>('/api/case-studies/sync');
      if (res) {
        setCards(res.cards);
        setSyncedAt(res.syncedAt);
        setError(null);
      }
    } catch (err) {
      // The cached list is still on screen and still usable, so this is a note
      // rather than a failure: refusing to show 46 case studies because the
      // 47th might exist would be the worse answer.
      setError(errorMessage(err));
    } finally {
      setSyncing(false);
    }
  }

  useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const res = await api.get<CaseStudyIndexResponse>('/api/case-studies');
        if (!live || !res) return;
        setCards(res.cards);
        setSyncedAt(res.syncedAt);
        // Auto-sync on open, which is what makes the list current without
        // anyone having to know there is a list. Only when it is actually
        // stale, and never in front of the cache.
        if (res.stale && !asked.current) {
          asked.current = true;
          void sync();
        }
      } catch (err) {
        if (live) setError(errorMessage(err));
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => {
      live = false;
    };
  }, []);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return cards;
    return cards.filter((c) => c.client.toLowerCase().includes(q) || c.title.toLowerCase().includes(q));
  }, [cards, query]);

  if (picked) {
    return (
      <div className="case-study__chosen">
        <div className="case-study__chosen-card">
          {picked.thumb && <img className="case-study__chosen-thumb" src={picked.thumb} alt="" loading="lazy" />}
          <div className="case-study__chosen-text">
            <strong className="case-study__chosen-client">{picked.client}</strong>
            <span className="case-study__chosen-title">{picked.title}</span>
            <span className="case-study__chosen-found">
              {importing ? (
                <>
                  <span className="new-deck__spinner new-deck__spinner--inline" aria-hidden="true" /> Reading the page
                  and fetching its pictures and clips…
                </>
              ) : importError ? (
                importError
              ) : imported ? (
                found(imported)
              ) : null}
            </span>
          </div>
          <button
            type="button"
            className="iconbtn"
            onClick={onClear}
            disabled={disabled || importing}
            aria-label="Pick a different case study"
            title="Pick a different case study"
          >
            <X />
          </button>
        </div>
        {!!imported?.warnings.length && (
          <ul className="case-study__warnings">
            {imported.warnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  return (
    <div className="case-study">
      <div className="case-study__bar">
        <input
          className="case-study__search"
          type="search"
          placeholder="Search case studies"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          disabled={disabled || loading}
          aria-label="Search case studies"
        />
        <span className="case-study__stamp">
          {syncing ? (
            <>
              <span className="new-deck__spinner new-deck__spinner--inline" aria-hidden="true" /> Updating the list…
            </>
          ) : (
            <>
              Checked {ago(syncedAt)}
              <button type="button" className="link small" onClick={() => void sync()} disabled={disabled}>
                Refresh
              </button>
            </>
          )}
        </span>
      </div>

      {error && <p className="new-deck__notice">{error}</p>}

      {loading ? (
        <p className="case-study__empty">
          <span className="new-deck__spinner new-deck__spinner--inline" aria-hidden="true" /> Loading the case studies…
        </p>
      ) : !cards.length ? (
        <p className="case-study__empty">No case studies yet. {syncing ? 'Fetching them now…' : 'Try Refresh.'}</p>
      ) : !shown.length ? (
        <p className="case-study__empty">Nothing matches “{query.trim()}”.</p>
      ) : (
        <ul className="case-study__grid">
          {shown.map((card) => (
            <li key={card.slug}>
              <button
                type="button"
                className="case-study__card"
                onClick={() => onPick(card)}
                disabled={disabled}
                title={card.title}
              >
                {card.thumb ? (
                  <img className="case-study__thumb" src={card.thumb} alt="" loading="lazy" />
                ) : (
                  <span className="case-study__thumb case-study__thumb--none" aria-hidden="true" />
                )}
                <span className="case-study__card-text">
                  <span className="case-study__card-client">{card.client}</span>
                  <span className="case-study__card-title">{card.title}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** "6 sections, 3 clips and 2 pictures" — what the import actually got, so the
 *  author can tell a page that came through whole from one that did not. */
function found(r: CaseStudyImportResponse): string {
  const bits = [`${r.headings.length} section${r.headings.length === 1 ? '' : 's'}`];
  if (r.videos) bits.push(`${r.videos} clip${r.videos === 1 ? '' : 's'}`);
  if (r.images) bits.push(`${r.images} picture${r.images === 1 ? '' : 's'}`);
  if (!r.videos && !r.images) bits.push('no pictures or clips');
  const last = bits.pop();
  return `Found ${bits.length ? `${bits.join(', ')} and ${last}` : last}.`;
}
